import { Avatar, MenuItem, MenuGroup } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { flowEditorState } from '../../flowEditorState';
import { useMemo } from 'react';

// Collect every persona referenced anywhere in the flow. We read from the React
// Flow node data (the same source the save path serialises) rather than the
// live editor instances: with viewport virtualisation only on-screen nodes have
// a mounted editor, so iterating editors would miss personas used off-screen.
// Use getNodes() rather than toObject(): toObject() deep-clones the entire flow
// (nodes, edges, viewport) on every menu open, which is expensive on large
// conversations; getNodes() returns the live node array we already need.
function getUsedPersonaIds(): Set<string> {
  const ids = new Set<string>();

  const nodes = flowEditorState.flowEditor?.getNodes();
  if (!nodes) {
    return ids;
  }

  for (const node of nodes) {
    const tiptap = node.data?.tiptap;
    const blocks = Array.isArray(tiptap?.content) ? tiptap.content : [];
    for (const block of blocks) {
      if (block?.type === 'dBlock' && block.attrs?.personaId) {
        ids.add(String(block.attrs.personaId));
      }
    }
  }

  return ids;
}

export function PersonasMenuList({
  onSelected,
  search,
}: {
  onSelected: (personaId: string) => void;
  search: string;
}) {
  const { data: contentPersonas } = trpcAdmin.contentPersonas.all.useQuery();

  const usedPersonaIds = useMemo(() => getUsedPersonaIds(), []);

  const filtered = useMemo(() => {
    if (!contentPersonas) {
      return { used: [], other: [] };
    }
    const term = search.toLowerCase();
    const matching =
      term.length > 0
        ? contentPersonas.filter((p) => p.name.toLowerCase().includes(term))
        : contentPersonas;

    const used: typeof contentPersonas = [];
    const other: typeof contentPersonas = [];
    for (const persona of matching) {
      if (usedPersonaIds.has(persona.id)) {
        used.push(persona);
      } else {
        other.push(persona);
      }
    }
    const byName = (a: (typeof matching)[number], b: (typeof matching)[number]) =>
      a.name.localeCompare(b.name);
    return { used: used.sort(byName), other: other.sort(byName) };
  }, [contentPersonas, search, usedPersonaIds]);

  const renderPersonaItem = (persona: NonNullable<typeof contentPersonas>[number]) => (
    <MenuItem
      onClick={() => {
        flowEditorState.defaultPersonaId = persona.id;
        onSelected(persona.id);
      }}
      key={persona.id}
      icon={<Avatar size="sm" name={persona.name} src={persona.avatarUrl ?? undefined} />}
    >
      {persona.name}
    </MenuItem>
  );

  return (
    <>
      {filtered.used.length > 0 ? (
        <MenuGroup color="gray.400" title="IN THIS CONVERSATION">
          {filtered.used.map(renderPersonaItem)}
        </MenuGroup>
      ) : null}
      {filtered.other.length > 0 ? (
        <MenuGroup color="gray.400" title="ALL PERSONAS">
          {filtered.other.map(renderPersonaItem)}
        </MenuGroup>
      ) : null}
    </>
  );
}
